// Maps a raw backend `campaignStatus` to the normalized status the UI shows (badge, filters, running-set).
// Pure + dependency-free so it can be unit-tested and reused everywhere without pulling in the thunks module.
//
// Completion philosophy (see also campaign-progress.ts): a message that has been SENT to WhatsApp is done
// from the campaign's perspective. So `complete-sent` (all messages sent, some maybe not delivery-acked)
// is a Completed state — we do NOT keep it "Running" waiting for delivery acks that, for groups and
// privacy-restricted recipients, never arrive. Per-message delivery detail stays in the logs view.

export type RealCampaignStatus = 'Pending' | 'Running' | 'Paused' | 'Completed' | 'Aborted';

// FE#256: a non-recurring campaign that resolves to Running but has had NO activity for longer than
// this window has genuinely ended — the backend simply never wrote a terminal status (there is no
// server-side `expired` concept on prod). 24h is deliberately conservative: a single campaign sends
// its whole audience in minutes-to-hours, so 24h of frozen `updatedAt` on a live-status campaign is
// unambiguously "not running anymore" — while never catching a campaign that is still progressing.
export const CAMPAIGN_STALE_RUNNING_MS = 24 * 60 * 60 * 1000;

export interface CampaignEndednessInput {
    campaignStatus: string;
    isCompletedSendSupported: boolean;
    isRecurring?: boolean;
    // The campaign's last write (its `updatedAt`, falling back to the schedule due date). A frozen
    // value far in the past is the tell that the run finished and the status was never advanced.
    lastActivityAt?: string | Date | number | null;
}

// True when a campaign is displayed as Running but has actually ENDED (see FE#256). Pure + gated so it
// can NEVER mislabel a live campaign: it only fires for a Running-resolving, non-recurring campaign
// whose last activity is older than CAMPAIGN_STALE_RUNNING_MS. Recurring campaigns (which idle between
// runs) and anything without a usable timestamp are left untouched.
export const isCampaignEndedButRunning = (
    input: CampaignEndednessInput,
    now: Date,
): boolean => {
    const { campaignStatus, isCompletedSendSupported, isRecurring, lastActivityAt } = input;

    // Only ever promote a state that currently reads as Running — leave pending/paused/terminal alone.
    if (getCampaignRealStatus(campaignStatus, isCompletedSendSupported) !== 'Running') return false;

    // A recurring campaign legitimately sits idle between runs; staleness says nothing about it.
    if (isRecurring) return false;

    if (lastActivityAt === undefined || lastActivityAt === null) return false;
    const last = new Date(lastActivityAt).getTime();
    if (Number.isNaN(last)) return false;

    return now.getTime() - last >= CAMPAIGN_STALE_RUNNING_MS;
};

// The lowercase lifecycle vocabulary the card UI (STATUS_MAP / StatusLabel / filter buckets) renders.
// Mirrors the `CampaignStatus` union in campaigns-redesign/primitives.tsx — kept as a plain string
// literal type here so this module stays dependency-free (no import of the extension component tree).
export type CampaignCardStatus =
    | 'draft' | 'pending' | 'active' | 'running' | 'paused' | 'completed' | 'aborted' | 'expired';

// The minimal shape needed to decide a card's status. management-list adapts a full `Campaign` onto
// this (recurrence lives on `schedule.isRecurring`; last activity is `updatedAt` ?? `schedule.dueDate`).
export interface CampaignCardInput {
    campaignStatus?: string;
    contactCount?: number;
    isRecurring?: boolean;
    lastActivityAt?: string | Date | number | null;
}

// Pure raw-`campaignStatus` → card-status resolution (extracted from management-list.campaignCardStatus
// so the whole path — including the FE#256 `expired` promotion — is unit-testable end-to-end without
// importing the extension component tree). A fully-sent campaign is Completed; a non-recurring campaign
// frozen at `running` past the stale window is Expired; everything else maps through getCampaignRealStatus.
export const resolveCampaignCardStatus = (
    input: CampaignCardInput,
    now: Date,
): CampaignCardStatus => {
    const raw: string = input.campaignStatus ?? '';

    // A fully-sent campaign is DONE — green success (all-confirmed is a valid completion). getCampaignRealStatus
    // still reports 'complete-sent' as "Running" (legacy waiting-for-delivery model), so short-circuit here.
    if (raw === 'complete-sent' || raw === 'complete-delivered' || raw === 'complete-send' || raw === 'completed') {
        return 'completed';
    }

    const isCompletedSendSupported = !!input.contactCount;
    const real = getCampaignRealStatus(raw, isCompletedSendSupported);

    // FE#256: a non-recurring campaign the backend left frozen at `running` has genuinely ended — surface
    // Expired instead of lying in Running forever. Gated so a live/recurring campaign is never mislabeled.
    if (
        real === 'Running' &&
        isCampaignEndedButRunning(
            {
                campaignStatus: raw,
                isCompletedSendSupported,
                isRecurring: input.isRecurring,
                lastActivityAt: input.lastActivityAt,
            },
            now,
        )
    ) {
        return 'expired';
    }

    switch (real) {
        case 'Completed': return 'completed';
        case 'Running': return 'running';
        case 'Paused': return 'paused';
        case 'Aborted': return 'aborted';
        case 'Pending': return 'pending';
        default: return 'draft';
    }
};

export const getCampaignRealStatus = (
    orignalCampaignStatus: string,
    isCompletedSendSupported: boolean,
    _lastActivityAt?: string,
): RealCampaignStatus | null => {
    // All finished states → Completed. `complete-sent` = all sent (done); `complete-delivered` = all
    // delivered; `complete-send` = legacy alias.
    if (
        orignalCampaignStatus === 'complete-delivered' ||
        orignalCampaignStatus === 'complete-sent' ||
        orignalCampaignStatus === 'complete-send'
    ) {
        return 'Completed';
    }

    if (orignalCampaignStatus === 'pending') {
        return 'Pending';
    }

    if (isCompletedSendSupported && orignalCampaignStatus === 'completed') {
        return 'Running';
    }

    if (orignalCampaignStatus === 'running') {
        return 'Running';
    }

    if (orignalCampaignStatus === 'aborted') {
        return 'Aborted';
    }

    if (orignalCampaignStatus === 'paused') {
        return 'Paused';
    }

    return null;
};
